// React import
import React, { useState, useEffect } from 'react';

// Generic imports
import _ from 'lodash';
import axios from 'axios';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faCircleCheck, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';

// MUI imports
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { green, red } from '@mui/material/colors';

// Local import
import SendEmailUsingExternalServiceDialog from './SendEmailUsingExternalServiceDialog';
import email from '../utils/email';
import readableId from '../../ReadableId.js';
import { getDefaultEmailBody, getEmailSubject } from '../opportunities/OpportunityOneOffEmailBuilder.utils.js';

function SendOpportunityReplyDialog(props) {
    const { org, accesstoken, emailLoginInfo, onEmailRefreshConnection, onEmailLogout, accounts, 
        conversationId, onSent, onClose, initialBody, opportunity, _email = email, _axios = axios, 
        attachPDF, setAttachPDF } = props;

    const [ isSending, setIsSending ] = useState(false);
    const [ isDrafting, setIsDrafting ] = useState(false);
    const [ messages, setMessages ] = useState(null);
    const [ error, setError ] = useState(null);
    const [ sent, setSent ] = useState(false);
    const [ link, setLink ] = useState(null);
    const [ forceNewThread, setForceNewThread ] = useState(false);
    const [ toRecipients, setToRecipients ] = useState(null);
    const [ ccRecipients, setCcRecipients ] = useState(null);
    const [ queuedEmailId, setQueuedEmailId ] = useState(null);
    const [ isPolling, setIsPolling ] = useState(false);
    const [ pendingRecipients, setPendingRecipients ] = useState({ toRecipients: [], ccRecipients: [] });
    const [ includeThread, setIncludeThread ] = useState(_.get(org, 'integrations.email.includeOriginalThread', true));

    // Helper function to ensure Poppins font is included in email body
    const ensurePoppinsFont = (html) => {
        if (!html) return html;
        // If body already has font-family, use it as-is
        if (html.includes('font-family') || html.includes('fontFamily')) {
            return html;
        }
        // Wrap in a div with Poppins font
        return `<div style="font-family: 'Poppins', sans-serif;">${html}</div>`;
    };

    async function apiSend({ apiFunc, body, subject: _subject, toRecipients: _toRecipients, ccRecipients: _ccRecipients }) {
        setError(null);
        try {
            // Ensure Poppins font is included in the body
            const bodyWithFont = ensurePoppinsFont(body);
            const pdfOption = attachPDF ? { opportunityId: opportunity.id, minimized: false } : null;

            if (forceNewThread) {
                let result = null;
                if (apiFunc === 'reply') {
                    result = await email.sendEmail({
                        org,
                        tokens: emailLoginInfo.tokens,
                        toRecipients: _toRecipients,
                        ccRecipients: _ccRecipients,
                        subject: _subject,
                        body: bodyWithFont,
                        attachments: [],
                        monadaAccesstoken: accesstoken,
                        monadaOrganizationId: org.id,
                        useQueue: true,
                        generatePdf: pdfOption,
                        opportunityId: opportunity.id,
                    });

                    // Start polling for email status
                    setQueuedEmailId(result.id);
                    setPendingRecipients({ toRecipients: _toRecipients, ccRecipients: _ccRecipients });
                    setIsPolling(true);
                    const pollResult = await email.waitForEmailSent({
                        org,
                        emailId: result.id,
                        monadaAccesstoken: accesstoken
                    });

                    setIsPolling(false);

                    if (pollResult.success) {
                        setSent(true);
                        onSent([{ conversationId: pollResult.email.conversationId, internetMessageId: pollResult.email.internetMessageId, toRecipients: _toRecipients, ccRecipients: _ccRecipients }]);
                    } else {
                        setError(pollResult.error || 'Failed to send email');
                        setIsSending(false);
                    }
                } else {
                    result = await email.createDraft({
                        org,
                        tokens: emailLoginInfo.tokens,
                        toRecipients: _toRecipients,
                        ccRecipients: _ccRecipients,
                        subject: _subject,
                        body: bodyWithFont,
                        attachments: [],
                        monadaAccesstoken: accesstoken,
                        monadaOrganizationId: org.id
                    });

                    setSent(true);
                    onSent([{ conversationId: result.conversationId, internetMessageId: result.internetMessageId, toRecipients: _toRecipients, ccRecipients: _ccRecipients }]);
                }
            } else {
                console.log('replying to message', messages[0].id, messages[0].conversationId, _toRecipients, _ccRecipients, bodyWithFont);
                if (apiFunc === 'reply') {
                    const result = await _email.sendReply({
                        org,
                        tokens: emailLoginInfo.tokens,
                        messageId: messages[0].id,
                        conversationId: messages[0].conversationId,
                        toRecipients: _toRecipients,
                        ccRecipients: _ccRecipients,
                        subject: _subject,
                        body: bodyWithFont,
                        attachments: [],
                        monadaAccesstoken: accesstoken,
                        monadaOrganizationId: org.id,
                        useQueue: true,
                        generatePdf: pdfOption,
                        opportunityId: opportunity.id,
                        includeThread,
                    });

                    // Start polling for email status
                    setQueuedEmailId(result.id);
                    setPendingRecipients({ toRecipients: _toRecipients, ccRecipients: _ccRecipients });
                    setIsPolling(true);
                    const pollResult = await email.waitForEmailSent({
                        org,
                        emailId: result.id,
                        monadaAccesstoken: accesstoken
                    });

                    setIsPolling(false);

                    if (pollResult.success) {
                        setSent(true);
                        onSent([{ toRecipients: _toRecipients, ccRecipients: _ccRecipients, conversationId: pollResult.email.conversationId || messages[0].conversationId, internetMessageId: pollResult.email.internetMessageId || messages[0].internetMessageId }]);
                    } else {
                        setError(pollResult.error || 'Failed to send email');
                        setIsSending(false);
                    }
                } else {
                    await _email.createDraftReply({
                        org,
                        tokens: emailLoginInfo.tokens,
                        messageId: messages[0].id,
                        conversationId: messages[0].conversationId,
                        toRecipients: _toRecipients,
                        ccRecipients: _ccRecipients,
                        body: bodyWithFont,
                        attachments: [],
                        monadaAccesstoken: accesstoken, monadaOrganizationId: org.id });

                    setSent(true);
                    onSent([{ toRecipients: _toRecipients, ccRecipients: _ccRecipients, conversationId: messages[0].conversationId, internetMessageId: messages[0].internetMessageId }])
                }
            }
        } catch (e) {
            console.error(e);
            setError(e.message);
            setIsSending(false);
            setIsDrafting(false);
            setIsPolling(false);
        }
    }

    const handleContinueInBackground = () => {
        // Call onSent with the queued email info so the parent knows it was queued
        onSent([{ queued: true, emailId: queuedEmailId, toRecipients: pendingRecipients.toRecipients, ccRecipients: pendingRecipients.ccRecipients }]);
        onClose();
    };

    const onSend = async ({ body, toRecipients, subject, ccRecipients }) => {
        const updatedBody = body.replace(/temporaryAccesstoken=[^"]*/g, `temporaryAccesstoken=${link}`);
        setIsSending(true);
        await apiSend({ apiFunc: 'reply', body: updatedBody, toRecipients, subject, ccRecipients });
    };

    useEffect(() => {
        if (emailLoginInfo?.status !== 'connected' || !conversationId) return;
        
        (async () => {
            try {
                console.log('conversationId', org, conversationId);
                const messages = await _email.getMessagesForConversation({ org, conversationId, tokens: emailLoginInfo.tokens });

                console.log('messages', messages, opportunity, org);

                if (_.isEmpty(messages)) {
                    setError('not-owner');
                    setMessages(null);
                    return;
                }
                let _toRecipients = messages ? _.filter([
                    messages[0].sender.emailAddress,
                    ...messages[0].toRecipients.map(r => r.emailAddress)
                ], a => a.address !== emailLoginInfo.email) : [];

                const _ccRecipients = messages ? _.filter(
                    messages[0].ccRecipients.map(r => r.emailAddress),
                    a => a.address !== emailLoginInfo?.email
                ) : [];

                if (_.isEmpty(_ccRecipients) && _.isEmpty(_toRecipients)) {
                    _toRecipients = [messages[0].sender.emailAddress];
                }

                setToRecipients(_toRecipients);
                setCcRecipients(_ccRecipients);
                setMessages(messages);
            } catch (e) {
                if (e?.result?.error?.code === 404 || e.message === 'invalid-request') {
                    if (!forceNewThread) {
                        setError('not-owner');
                        setMessages(null);
                    }
                } else {
                    onEmailRefreshConnection();
                }
            }
        })();
    }, [emailLoginInfo?.status, conversationId]);

    useEffect(() => {
        if (!opportunity) return;
        (async () => {
            const { data: _link } = await _axios.get(`/api/opportunities/${opportunity.id}/offer-url?accesstoken=${accesstoken}&type=end-customer&organizationId=${org.id}`);
            setLink(_link);
        })();
    }, [opportunity]);

    if (error === 'not-owner' && !forceNewThread) return (
        <Box sx={{ maxWidth: '75vw', minWidth: '75vw', p: 3 }} >
            <Box sx={{ padding: '24px 0px', color: red[500], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FontAwesomeIcon icon={faCircleXmark} beat size='4x' style={{ animationIterationCount: 1 }} />
            </Box>
            <Box sx={{ fontWeight: 800, color: red[500], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                We can't find the original email.
            </Box>
            <Box sx={{ fontWeight: 400, fontSize: '0.8em', color: red[500], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                You probably took ownership of this opportunity from someone else.
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
                <Button 
                    variant='text' 
                    onClick={() => { setError(null); setForceNewThread(true) }} 
                    color='primary' 
                    sx={{ textTransform: 'none', textDecoration: 'underline !important', opacity: 0.8, fontSize: '0.8em' }}
                >
                    Send the reply as a new thread from my mailbox
                </Button>
            </Box>
            <Box sx={{ display: 'flex', marginTop: '24px', flexDirection: 'row-reverse', gap: 1 }}>
                <Button
                    color='error'
                    variant='contained'
                    onClick={onClose}
                >
                    Close
                </Button>
            </Box>
        </Box>
    );

    if (!forceNewThread && !messages && emailLoginInfo?.status === 'connected') return (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
            <Typography variant='h6' sx={{ mb: 2 }}>Loading original email...</Typography>
            <CircularProgress />
        </Box>
    );

    if (isPolling) return (
        <Box sx={{ maxWidth: '75vw', minWidth: '75vw', p: 3 }} >
            <Box sx={{ padding: '24px 0px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={64} />
            </Box>
            <Box sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Your email is being sent...
            </Box>
            <Box sx={{ display: 'flex', marginTop: '24px', flexDirection: 'row-reverse' }}>
                <Button
                    color='primary'
                    variant='outlined'
                    onClick={handleContinueInBackground}
                >
                    Continue in background...
                </Button>
            </Box>
        </Box>
    )

    if (sent) return (
        <Box sx={{ maxWidth: '75vw', minWidth: '75vw', p: 3 }} >
            <Box sx={{ padding: '24px 0px', color: green[500], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FontAwesomeIcon icon={faCircleCheck} beat size='4x' style={{ animationIterationCount: 1 }} />
            </Box>
            <Box sx={{ fontWeight: 800, color: green[500], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isDrafting ? 'Draft created successfully!' : 'Email sent successfully!'}
            </Box>
            <Box sx={{ display: 'flex', marginTop: '24px', flexDirection: 'row-reverse' }}>
                <Button
                    color='success'
                    variant='outlined'
                    onClick={() => onClose()}
                >
                    Close
                </Button>
            </Box>
        </Box>
    )

    const subject = forceNewThread || _.isEmpty(messages) ?
            getEmailSubject({ organization: org, opportunity, readableId: readableId(opportunity) })
        : messages[0].subject;
    return (
        <SendEmailUsingExternalServiceDialog
            org={org}
            accesstoken={accesstoken}
            selectedAccountId={opportunity.account?.id}
            emailLoginInfo={emailLoginInfo}
            accounts={accounts}
            onEmailRefreshConnection={onEmailRefreshConnection}
            onEmailLogout={onEmailLogout}
            initialSubject={subject}
            initialBody={initialBody ? initialBody.replace(/temporaryAccesstoken=[^"]*/g, `temporaryAccesstoken=${link}`) :
                getDefaultEmailBody({ 
                    organization: org, 
                    transportationMethod: opportunity.transportationMethod, 
                    link: `https://app.monada.ai/offers?temporaryAccesstoken=${link}` 
                })
            }
            toRecipients={forceNewThread ? [] : toRecipients}
            ccRecipients={forceNewThread ? [] : ccRecipients}
            receipientEditable={'multiple'}
            buttons={[{
                variant: 'contained',
                color: 'primary',
                func: onSend,
                sx: { width: '200px', minWidth: '200px' },
                disabled: isSending || isDrafting,
                children: isSending ? <CircularProgress size={16} /> :
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FontAwesomeIcon icon={faPaperPlane} />
                        &nbsp;
                        Send
                    </Box>

            }]}
            editorFooter={(
                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={attachPDF}
                                onChange={e => setAttachPDF(e.target.checked)}
                                color='primary'
                                size='small'
                            />
                        }
                        label={
                            <Box>
                                <Typography variant='body2'>Add the offer as a PDF attachment.</Typography>
                                {attachPDF && opportunity?.attributes?.viewTemplate && (
                                    <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', marginTop: '4px' }}>
                                        Template: {opportunity.attributes.viewTemplate === 'contract' ? 'Contract' : 'Standard'}
                                    </Typography>
                                )}
                            </Box>
                        }
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includeThread}
                                onChange={e => setIncludeThread(e.target.checked)}
                                color='primary'
                                size='small'
                            />
                        }
                        label={<Typography variant='body2'>Include original thread</Typography>}
                    />
                    {error ? <Alert severity='error'>{error}</Alert> : null}
                    {isSending && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 2 }}>
                            <Typography variant='caption' sx={{ width: '210px'}}>
                                Please don't close this window or refresh the page while we're sending the email.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        />
    )
}

export default SendOpportunityReplyDialog;